<template>
  <div class="sp-page mx-auto">
    <div class="sp-head">
      <div>
        <h5 class="sp-title">
          <i class="bi bi-headset"></i>
          Integrated Repair and Maintenance Support (IRMS) Services
        </h5>
        <p class="sp-subtitle">
          Report ICT problems, facility repairs, and other service needs, then
          follow each ticket here.
        </p>
      </div>
      <div class="sp-head-actions">
        <button v-if="abilities.report" type="button" class="btn btn-outline-primary" @click="openReport">
          <i class="bi bi-file-earmark-bar-graph mr-1"></i> Report
        </button>
        <button type="button" class="btn btn-primary" @click="openCreate">
          <i class="bi bi-plus-lg mr-1"></i> New ticket
        </button>
      </div>
    </div>

    <CCard class="sp-card shadow-sm">
      <CCardBody class="p-0">
        <div class="sp-toolbar">
          <div class="sp-tabs" role="tablist" aria-label="Filter tickets by status">
            <button
              v-for="f in statusTabs"
              :key="f.key"
              type="button"
              role="tab"
              class="sp-tab"
              :class="{ active: filter === f.key }"
              :aria-selected="filter === f.key ? 'true' : 'false'"
              @click="setFilter(f.key)"
            >
              {{ f.label }}
              <span class="sp-tab-count">{{ counts[f.key] || 0 }}</span>
            </button>
          </div>
          <div
            v-if="scopes.length > 1"
            class="sp-seg"
            role="group"
            aria-label="Whose tickets to show"
          >
            <button
              v-for="s in scopes"
              :key="s.key"
              type="button"
              :class="{ on: scope === s.key }"
              :aria-pressed="scope === s.key ? 'true' : 'false'"
              @click="setScope(s.key)"
            >
              {{ s.label }}
              <span v-if="s.key === 'assigned' && assignedOpen" class="sp-seg-count">{{ assignedOpen }}</span>
            </button>
          </div>
        </div>

        <div class="sp-controls">
          <div class="sp-search">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input
              v-model="search"
              type="search"
              class="form-control"
              :placeholder="searchPlaceholder"
              aria-label="Search tickets"
            />
          </div>
          <div class="sp-controls-right">
            <button
              type="button"
              class="btn btn-outline-secondary text-dark sp-filter-btn"
              :class="{ 'is-open': showFilters }"
              aria-controls="sp-filter-panel"
              :aria-expanded="showFilters ? 'true' : 'false'"
              @click="showFilters = !showFilters"
            >
              <i class="bi bi-funnel mr-1"></i> Filters
              <span v-if="activeFilterCount" class="sp-filter-count">{{ activeFilterCount }}</span>
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary text-dark"
              title="Refresh"
              aria-label="Refresh"
              :disabled="refreshing"
              @click="refresh()"
            >
              <i class="bi bi-arrow-repeat" :class="{ 'sp-spin': refreshing }"></i>
            </button>
          </div>
        </div>

        <div v-if="showFilters" id="sp-filter-panel" class="sp-filters">
          <div class="sp-filter-field">
            <label for="sp-filter-type" class="sp-label">Service type</label>
            <select id="sp-filter-type" v-model="filterType" class="form-control form-control-sm" @change="applyFilters">
              <option value="">All types</option>
              <option v-for="t in typeOptions" :key="t.value" :value="t.value">
                {{ t.value }} ({{ t.count }})
              </option>
            </select>
          </div>
          <div class="sp-filter-field">
            <label for="sp-filter-unit" class="sp-label">Unit/Section</label>
            <select id="sp-filter-unit" v-model="filterUnit" class="form-control form-control-sm" @change="applyFilters">
              <option value="">All units</option>
              <option v-for="u in unitOptions" :key="u.value" :value="u.value">
                {{ unitLabel(u.value) }} ({{ u.count }})
              </option>
            </select>
          </div>
          <div v-if="showAssignee" class="sp-filter-field">
            <label for="sp-filter-assignee" class="sp-label">Assigned to</label>
            <select id="sp-filter-assignee" v-model="filterAssignee" class="form-control form-control-sm" @change="applyFilters">
              <option value="">Anyone</option>
              <option v-for="a in assigneeOptions" :key="a.value" :value="a.value">
                {{ a.label }} ({{ a.count }})
              </option>
            </select>
          </div>
          <div class="sp-filter-field sp-filter-dates">
            <span class="sp-label">Filed between</span>
            <div class="sp-date-range">
              <input
                v-model="filterFrom"
                type="date"
                class="form-control form-control-sm"
                aria-label="Filed from"
                :max="filterTo || null"
                @change="applyFilters"
              />
              <span>to</span>
              <input
                v-model="filterTo"
                type="date"
                class="form-control form-control-sm"
                aria-label="Filed until"
                :min="filterFrom || null"
                @change="applyFilters"
              />
            </div>
            <div class="sp-quick-periods">
              <button v-for="p in periods" :key="p.key" type="button" class="sp-link-btn" @click="setPeriod(p.key)">
                {{ p.label }}
              </button>
            </div>
          </div>
          <button v-if="activeFilterCount" type="button" class="btn btn-sm btn-link sp-filter-clear" @click="clearFilters">
            Clear filters
          </button>
        </div>
        <div v-else-if="activeFilterCount" class="sp-filter-chips">
          <span v-for="chip in filterChips" :key="chip.key" class="sp-chip">
            {{ chip.label }}
            <button type="button" :aria-label="`Remove filter: ${chip.label}`" @click="removeFilter(chip.key)">
              <i class="bi bi-x"></i>
            </button>
          </span>
          <button type="button" class="btn btn-sm btn-link" @click="clearFilters">Clear all</button>
        </div>

        <div v-if="selected.length" class="sp-selection">
          <span>
            <strong>{{ selected.length }}</strong>
            {{ selected.length === 1 ? "ticket" : "tickets" }} selected
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="!assignable.length || !!busy"
            @click="assignSelected"
          >
            <i class="bi bi-person-plus mr-1"></i> Assign
            <template v-if="assignable.length && assignable.length !== selected.length">
              ({{ assignable.length }})
            </template>
          </button>
          <button
            v-if="workable.length"
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="!forwardable.length || !!busy"
            @click="forwardSelected"
          >
            <span
              v-if="busy === 'forward'"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            <i v-else class="bi bi-box-arrow-up-right mr-1"></i>
            Forward to GSS
            <template v-if="forwardable.length && forwardable.length !== selected.length">
              ({{ forwardable.length }})
            </template>
          </button>
          <button
            v-if="workable.length"
            type="button"
            class="btn btn-sm btn-outline-success"
            :disabled="!closable.length || !!busy"
            @click="closeSelected"
          >
            <i class="bi bi-check2-circle mr-1"></i> Close
            <template v-if="closable.length && closable.length !== selected.length">
              ({{ closable.length }})
            </template>
          </button>
          <button type="button" class="btn btn-sm btn-link" @click="clearSelection">
            Clear
          </button>
          <span v-if="selectionNote" class="sp-selection-note">{{ selectionNote }}</span>
        </div>

        <div v-if="loadError" class="sp-error" role="alert">
          <i class="bi bi-exclamation-triangle"></i> {{ loadError }}
          <button type="button" class="btn btn-link" @click="refresh()">
            Try again
          </button>
        </div>

        <div v-if="loading" class="sp-state">
          <span class="spinner-border spinner-border-sm mr-1"></span>
          Loading tickets…
        </div>
        <div v-else-if="!tickets.length" class="sp-state">
          <i class="bi" :class="search ? 'bi-search' : 'bi-inbox'"></i>
          <div class="sp-state-title">{{ emptyTitle }}</div>
          <div>{{ emptyText }}</div>
          <button v-if="search" type="button" class="btn btn-link" @click="search = ''">
            Clear search
          </button>
          <button v-else-if="activeFilterCount" type="button" class="btn btn-link" @click="clearFilters">
            Clear filters
          </button>
          <button
            v-else-if="filter !== 'all' && counts.all"
            type="button"
            class="btn btn-link"
            @click="setFilter('all')"
          >
            Show all tickets
          </button>
          <button
            v-else-if="!counts.all && scope === 'mine'"
            type="button"
            class="btn btn-link"
            @click="openCreate"
          >
            File your first ticket
          </button>
        </div>

        <!-- Phones: a card per ticket -->
        <ul v-else-if="isPhone" class="sp-cards" :class="{ dim: refreshing }">
          <li
            v-for="ticket in tickets"
            :key="ticket.id"
            class="sp-card-row"
            :class="{ selected: isSelected(ticket), flash: ticket.id === flashId }"
            tabindex="0"
            :aria-label="rowLabel(ticket)"
            @click="openTicket(ticket)"
            @keydown.enter.self="openTicket(ticket)"
          >
            <div class="sp-card-check" @click.stop>
              <input
                v-if="canSelect(ticket)"
                type="checkbox"
                :checked="isSelected(ticket)"
                :aria-label="`Select ticket ${ticket.id}`"
                @change="toggleOne(ticket, $event.target.checked)"
              />
            </div>
            <span class="sp-type-icon sm" :class="typeOf(ticket).tone" aria-hidden="true">
              <i class="bi" :class="typeOf(ticket).icon"></i>
            </span>
            <div class="sp-card-body">
              <div class="sp-card-top">
                <span class="sp-request-type">{{ ticket.type }}</span>
                <span class="sp-pill" :style="pillStyle(statusMeta(ticket.status))">
                  <i class="bi" :class="statusMeta(ticket.status).icon"></i>
                  {{ statusMeta(ticket.status).label }}
                </span>
              </div>
              <div class="sp-excerpt">{{ ticket.excerpt }}</div>
              <div v-if="abilities.handle && ticket.needs_assignment" class="sp-needs-tech">
                <i class="bi bi-person-plus" aria-hidden="true"></i> Needs a technician
              </div>
              <div class="sp-card-meta">
                <span>#{{ ticket.id }}</span>
                <span>{{ unitLabel(ticket.unit) }}</span>
                <span v-if="showRequester">{{ ticket.creator_name || "—" }}</span>
                <span v-if="showAssignee && ticket.assigned_name">Assigned to {{ ticket.assigned_name }}</span>
                <span class="sp-card-when" :class="{ late: activity(ticket).late }">
                  {{ activity(ticket).short }}
                </span>
              </div>
            </div>
          </li>
        </ul>

        <!-- Larger screens: a table -->
        <div v-else class="table-responsive">
          <table class="table sp-table" :class="{ dim: refreshing }">
            <thead>
              <tr>
                <th class="sp-check">
                  <input
                    ref="selectAll"
                    type="checkbox"
                    :checked="allSelected"
                    :disabled="!selectableRows.length"
                    aria-label="Select all tickets on this page"
                    @change="toggleAll($event.target.checked)"
                  />
                </th>
                <th scope="col">Request</th>
                <th scope="col">Unit</th>
                <th v-if="showRequester" scope="col">Requested by</th>
                <th v-if="showAssignee" scope="col">Assigned to</th>
                <th scope="col">Status</th>
                <th scope="col" aria-sort="descending" title="Newest first: when it was closed, or else when it was filed">
                  Last activity <i class="bi bi-arrow-down-short" aria-hidden="true"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="ticket in tickets"
                :key="ticket.id"
                class="sp-row"
                :class="{
                  selected: isSelected(ticket),
                  flash: ticket.id === flashId,
                }"
                tabindex="0"
                :aria-label="rowLabel(ticket)"
                @click="openTicket(ticket)"
                @keydown.enter.self="openTicket(ticket)"
              >
                <td class="sp-check" @click.stop>
                  <input
                    v-if="canSelect(ticket)"
                    type="checkbox"
                    :checked="isSelected(ticket)"
                    :aria-label="`Select ticket ${ticket.id}`"
                    @change="toggleOne(ticket, $event.target.checked)"
                  />
                </td>
                <td class="sp-request">
                  <div class="sp-request-wrap">
                    <span class="sp-type-icon sm" :class="typeOf(ticket).tone" aria-hidden="true">
                      <i class="bi" :class="typeOf(ticket).icon"></i>
                    </span>
                    <div class="sp-request-text">
                      <div class="sp-request-type">
                        {{ ticket.type }} <span class="sp-id">#{{ ticket.id }}</span>
                      </div>
                      <div class="sp-excerpt" :title="ticket.excerpt">
                        {{ ticket.excerpt }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="sp-nowrap">{{ unitLabel(ticket.unit) }}</td>
                <td v-if="showRequester" class="sp-nowrap">
                  {{ ticket.creator_name || "—" }}
                </td>
                <td v-if="showAssignee" class="sp-nowrap">
                  {{ ticket.assigned_name || "—" }}
                </td>
                <td>
                  <span class="sp-pill" :style="pillStyle(statusMeta(ticket.status))">
                    <i class="bi" :class="statusMeta(ticket.status).icon"></i>
                    {{ statusMeta(ticket.status).label }}
                  </span>
                  <div v-if="abilities.handle && ticket.needs_assignment" class="sp-needs-tech mt-1">
                    <i class="bi bi-person-plus" aria-hidden="true"></i> Needs a technician
                  </div>
                  <div
                    v-else-if="ticket.has_findings && ticket.status !== 'Closed'"
                    class="sp-sub mt-1"
                  >
                    findings recorded
                  </div>
                </td>
                <td>
                  <div class="sp-nowrap">{{ formatDate(activity(ticket).date) }}</div>
                  <div class="sp-sub" :class="{ late: activity(ticket).late }">
                    {{ activity(ticket).note }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="sp-footer">
          <div class="sp-rows">
            <label for="support-ticket-rows">Rows</label>
            <select
              id="support-ticket-rows"
              v-model.number="perPage"
              class="form-control form-control-sm"
              @change="changePerPage"
            >
              <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
          <div class="sp-pager">
            <span>{{ rangeLabel }}</span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary text-dark"
              aria-label="Previous page"
              :disabled="pagination.current_page <= 1 || refreshing"
              @click="goToPage(pagination.current_page - 1)"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary text-dark"
              aria-label="Next page"
              :disabled="pagination.current_page >= pagination.last_page || refreshing"
              @click="goToPage(pagination.current_page + 1)"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CreateTicketFormModal
      ref="CreateTicketFormModal"
      :default-unit="lastUnit"
      @created="onCreated"
    ></CreateTicketFormModal>

    <ViewTicketDetailsModal
      ref="ViewTicketDetailsModal"
      :can-manage-referrals="abilities.manage_referrals"
      @changed="refresh({ keepSelection: true })"
      @hidden="clearTicketQuery"
    ></ViewTicketDetailsModal>

    <CloseTicketModal
      ref="CloseTicketModal"
      :current-user-id="currentUserId"
      @closed="onBulkClosed"
    ></CloseTicketModal>

    <AssignTicketModal ref="AssignTicketModal" @assigned="onBulkAssigned"></AssignTicketModal>

    <TicketReportModal v-if="abilities.report" ref="TicketReportModal" :facets="facets"></TicketReportModal>
  </div>
</template>

<script>
import "../support.css";
import CreateTicketFormModal from "./components/CreateTicketFormModal.vue";
import ViewTicketDetailsModal from "./components/ViewTicketDetailsModal.vue";
import CloseTicketModal from "./components/CloseTicketModal.vue";
import AssignTicketModal from "./components/AssignTicketModal.vue";
import TicketReportModal from "./components/TicketReportModal.vue";
import {
  PERIODS,
  TICKET_FILTERS,
  UNITS,
  daysSince,
  errorMessage,
  formatDate,
  periodRange,
  pillStyle,
  rangeLabel,
  serviceType,
  shortAge,
  shortDuration,
  ticketStatusMeta,
} from "../supportOptions";

const WAITING_LONG_DAYS = 7;

export default {
  name: "SupportTickets",
  components: {
    CreateTicketFormModal,
    ViewTicketDetailsModal,
    CloseTicketModal,
    AssignTicketModal,
    TicketReportModal,
  },
  data() {
    return {
      tickets: [],
      counts: {},
      abilities: { handle: false, assigned: false, manage_referrals: false, report: false },
      assignedOpen: 0,
      lastUnit: "",
      filters: TICKET_FILTERS,
      filter: "open",
      // Empty until the first load: the server picks the default ("Assigned to me" when it applies).
      scope: "",
      search: "",
      // More filters: service type, unit, and the dates filed (YYYY-MM-DD).
      filterType: "",
      filterUnit: "",
      filterAssignee: "",
      filterFrom: "",
      filterTo: "",
      facets: { types: [], units: [], assignees: [] },
      showFilters: false,
      periods: PERIODS,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      pagination: { current_page: 1, last_page: 1, from: 0, to: 0, total: 0 },
      loading: true,
      refreshing: false,
      loadError: "",
      selected: [],
      busy: "",
      flashId: null,
      firstLoad: true,
      requestSeq: 0,
      searchTimer: null,
      isPhone: false,
    };
  },
  computed: {
    currentUserId() {
      const user = this.$store.state.user || {};
      return user.id || null;
    },
    // The desk's first step on a new ticket is assigning a technician: "To assign" comes first.
    statusTabs() {
      return this.abilities.handle ? [{ key: "to_assign", label: "To assign" }, ...this.filters] : this.filters;
    },
    // The support desk sees everyone's tickets; anyone with tickets assigned also gets "Assigned to me".
    scopes() {
      if (this.abilities.handle) {
        return [
          { key: "all", label: "Everyone's" },
          { key: "assigned", label: "Assigned to me" },
          { key: "mine", label: "Filed by me" },
        ];
      }
      if (this.abilities.assigned) {
        return [
          { key: "assigned", label: "Assigned to me" },
          { key: "mine", label: "Filed by me" },
        ];
      }
      return [{ key: "mine", label: "Filed by me" }];
    },
    // Of the selection, the tickets assigned to me: only those can be forwarded or closed.
    workable() {
      return this.selected.filter((ticket) => this.canWork(ticket));
    },
    closable() {
      return this.workable.filter((ticket) => ticket.status !== "Closed");
    },
    selectionNote() {
      if (!this.selected.length) return "";
      if (!this.workable.length) return "Only the technician assigned to a ticket can forward or close it.";
      if (!this.forwardable.length) return "Already with General Services.";
      return "";
    },
    showRequester() {
      return (this.abilities.handle && this.scope === "all") || this.scope === "assigned";
    },
    showAssignee() {
      return this.abilities.handle && this.scope === "all";
    },
    searchPlaceholder() {
      return this.showRequester
        ? "Search ticket no., request, unit, or name"
        : "Search ticket no., request, or unit";
    },
    selectableRows() {
      return this.tickets.filter((ticket) => this.canSelect(ticket));
    },
    allSelected() {
      return (
        this.selectableRows.length > 0 &&
        this.selectableRows.every((ticket) => this.isSelected(ticket))
      );
    },
    someSelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
    forwardable() {
      return this.selected.filter(
        (ticket) =>
          ticket.status !== "Closed" && ticket.status !== "Forwarded to GSS" && this.canWork(ticket)
      );
    },
    assignable() {
      return this.selected.filter(
        (ticket) => ticket.status !== "Closed" && (this.abilities.handle || this.canWork(ticket))
      );
    },
    rangeLabel() {
      const p = this.pagination;
      if (!p.total) return "0 tickets";
      return `${p.from}–${p.to} of ${p.total}`;
    },
    activeFilterCount() {
      return [this.filterType, this.filterUnit, this.showAssignee && this.filterAssignee, this.filterFrom || this.filterTo].filter(Boolean)
        .length;
    },
    filterChips() {
      const chips = [];
      if (this.filterType) chips.push({ key: "type", label: `Type: ${this.filterType}` });
      if (this.filterUnit) chips.push({ key: "unit", label: `Unit: ${this.unitLabel(this.filterUnit)}` });
      if (this.showAssignee && this.filterAssignee) {
        const who = (this.facets.assignees || []).find((a) => a.value === this.filterAssignee);
        chips.push({ key: "assignee", label: `Assigned to: ${who ? who.label : "someone"}` });
      }
      if (this.filterFrom || this.filterTo) {
        chips.push({ key: "dates", label: `Filed ${rangeLabel(this.filterFrom, this.filterTo)}` });
      }
      return chips;
    },
    // The facets cover the tickets in view; keep a chosen value listed even if it isn't.
    typeOptions() {
      return this.withSelected(this.facets.types || [], this.filterType);
    },
    unitOptions() {
      return this.withSelected(this.facets.units || [], this.filterUnit);
    },
    assigneeOptions() {
      return this.facets.assignees || [];
    },
    emptyTitle() {
      if (this.search) return "No tickets match your search";
      if (this.activeFilterCount && this.counts.all === 0) return "No tickets match these filters";
      if (!this.counts.all) {
        if (this.scope === "assigned") return "Nothing is assigned to you";
        return this.scope === "mine" ? "You haven't filed any tickets yet" : "No tickets yet";
      }
      const labels = {
        to_assign: "Every new ticket has a technician",
        open: "No open tickets",
        pending: "Nothing pending",
        in_progress: "Nothing in progress",
        on_hold: "Nothing on hold",
        forwarded: "Nothing with General Services",
        closed: "No closed tickets",
      };
      return labels[this.filter] || "No tickets";
    },
    emptyText() {
      if (this.search) return "Try a ticket number, a word from the request, or a unit.";
      if (this.activeFilterCount && this.counts.all === 0) return "Try a different type, unit or date range.";
      if (!this.counts.all) {
        if (this.scope === "assigned") return "Tickets someone assigns to you show up here.";
        return this.scope === "mine"
          ? "When something breaks or you need help, file a ticket and track it here."
          : "New tickets will show up here.";
      }
      if (this.filter === "to_assign") return "New tickets wait here until you assign someone to handle them.";
      return this.filter === "open" ? "Everything has been taken care of." : "";
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchTickets();
      }, 350);
    },
    someSelected: {
      handler(value) {
        this.$nextTick(() => {
          if (this.$refs.selectAll) this.$refs.selectAll.indeterminate = value;
        });
      },
      immediate: true,
    },
    "$route.query.ticket"() {
      this.openFromRoute();
    },
    "$route.query.new"() {
      this.openFromRoute();
    },
  },
  mounted() {
    // Phones get cards instead of a table that scrolls sideways.
    this.phoneQuery = window.matchMedia("(max-width: 767.98px)");
    this.isPhone = this.phoneQuery.matches;
    this.phoneQuery.addListener(this.onPhoneChange);
    this.fetchTickets().then(() => this.openFromRoute());
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    if (this.phoneQuery) this.phoneQuery.removeListener(this.onPhoneChange);
  },
  methods: {
    statusMeta: ticketStatusMeta,
    pillStyle,
    shortAge,
    shortDuration,
    formatDate,
    unitLabel(value) {
      const unit = UNITS.find((u) => u.value === value);
      return unit ? unit.label : value || "—";
    },
    withSelected(options, selected) {
      if (!selected || options.some((o) => o.value === selected)) return options;
      return [{ value: selected, count: 0 }, ...options];
    },
    typeOf(ticket) {
      return serviceType(ticket.type);
    },
    rowLabel(ticket) {
      return `Ticket ${ticket.id}: ${ticket.type}, ${this.statusMeta(ticket.status).label}`;
    },
    /** The list is sorted by this: when a ticket was closed, or else when it was filed. */
    activity(ticket) {
      // The list is sorted by the date filed, so that's the date shown.
      if (ticket.status === "On hold" && ticket.hold_since) {
        return {
          date: ticket.created_at,
          note: `Filed · on hold ${shortAge(ticket.hold_since)}`,
          short: `Filed ${formatDate(ticket.created_at)} · on hold ${shortAge(ticket.hold_since)}`,
          late: false,
        };
      }
      if (ticket.closed_at) {
        return {
          date: ticket.closed_at,
          note: `Closed · took ${shortDuration(ticket.created_at, ticket.closed_at)}`,
          short: `Closed ${formatDate(ticket.closed_at)}`,
          late: false,
        };
      }
      const age = shortAge(ticket.created_at);
      return {
        date: ticket.created_at,
        note: `Filed · open ${age}`,
        short: `Filed ${formatDate(ticket.created_at)} · open ${age}`,
        late: this.isWaitingLong(ticket),
      };
    },
    onPhoneChange(event) {
      this.isPhone = event.matches;
    },
    fetchTickets({ keepSelection = false } = {}) {
      const seq = ++this.requestSeq;
      this.refreshing = true;

      return axios
        .get("api/tickets", {
          params: {
            status: this.filter,
            scope: this.scope || undefined,
            search: this.search.trim() || undefined,
            type: this.filterType || undefined,
            unit: this.filterUnit || undefined,
            assignee: (this.showAssignee && this.filterAssignee) || undefined,
            from: this.filterFrom || undefined,
            to: this.filterTo || undefined,
            perPage: this.perPage,
            page: this.pagination.current_page || 1,
          },
        })
        .then((res) => {
          if (seq !== this.requestSeq) return null;
          const data = res.data || {};

          // Nothing open but some history: show everything on first visit.
          if (
            this.firstLoad &&
            this.filter === "open" &&
            data.counts &&
            !data.counts.open &&
            data.counts.all
          ) {
            this.firstLoad = false;
            this.filter = "all";
            return this.fetchTickets();
          }
          this.firstLoad = false;

          this.tickets = data.data || [];
          this.counts = data.counts || {};
          this.abilities = data.abilities || this.abilities;
          this.assignedOpen = data.assigned_open || 0;
          this.facets = data.facets || this.facets;
          this.lastUnit = data.last_unit || "";
          if (data.scope) this.scope = data.scope;
          this.pagination = {
            current_page: data.current_page || 1,
            last_page: data.last_page || 1,
            from: data.from || 0,
            to: data.to || 0,
            total: data.total || 0,
          };
          this.loadError = "";

          if (keepSelection) {
            const stillThere = this.tickets.filter((t) => this.canSelect(t));
            this.selected = this.selected
              .map((s) => stillThere.find((t) => t.id === s.id))
              .filter(Boolean);
          } else {
            this.selected = [];
          }
          return res;
        })
        .catch((err) => {
          if (seq !== this.requestSeq) return;
          this.loadError = errorMessage(err, "Couldn't load tickets.");
        })
        .finally(() => {
          if (seq !== this.requestSeq) return;
          this.loading = false;
          this.refreshing = false;
        });
    },
    refresh(options = {}) {
      return this.fetchTickets({ keepSelection: true, ...options });
    },
    setFilter(key) {
      if (this.filter === key) return;
      this.filter = key;
      // New tickets aren't anyone's yet, so the queue lives in everyone's tickets.
      if (key === "to_assign") this.scope = "all";
      this.pagination.current_page = 1;
      this.fetchTickets();
    },
    setScope(scope) {
      if (this.scope === scope) return;
      this.scope = scope;
      if (scope !== "all" && this.filter === "to_assign") this.filter = "open";
      this.pagination.current_page = 1;
      this.fetchTickets();
    },
    applyFilters() {
      this.pagination.current_page = 1;
      this.fetchTickets();
    },
    setPeriod(key) {
      const range = periodRange(key);
      if (!range) return;
      [this.filterFrom, this.filterTo] = range;
      this.applyFilters();
    },
    removeFilter(key) {
      if (key === "type") this.filterType = "";
      if (key === "unit") this.filterUnit = "";
      if (key === "assignee") this.filterAssignee = "";
      if (key === "dates") {
        this.filterFrom = "";
        this.filterTo = "";
      }
      this.applyFilters();
    },
    clearFilters() {
      this.filterType = "";
      this.filterUnit = "";
      this.filterAssignee = "";
      this.filterFrom = "";
      this.filterTo = "";
      this.applyFilters();
    },
    // Starts from the filters set on the list; the status tab is just a view, so it's left out.
    openReport() {
      this.$refs.TicketReportModal.open({
        type: this.filterType,
        unit: this.filterUnit,
        from: this.filterFrom,
        to: this.filterTo,
      });
    },
    changePerPage() {
      this.pagination.current_page = 1;
      this.fetchTickets();
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.fetchTickets();
    },

    // Tickets assigned to me can be forwarded or closed; the desk can also select any open one to assign.
    canSelect(ticket) {
      if (ticket.status === "Closed") return false;
      return this.canWork(ticket) || this.abilities.handle;
    },
    // Only the technician a ticket is assigned to works it (new tickets have nobody yet).
    canWork(ticket) {
      return ticket.assigned_to !== null && Number(ticket.assigned_to) === Number(this.currentUserId);
    },
    isSelected(ticket) {
      return this.selected.some((s) => s.id === ticket.id);
    },
    toggleOne(ticket, checked) {
      if (checked && !this.isSelected(ticket)) {
        this.selected.push(ticket);
      } else if (!checked) {
        this.selected = this.selected.filter((s) => s.id !== ticket.id);
      }
    },
    toggleAll(checked) {
      this.selected = checked ? this.selectableRows.slice() : [];
    },
    clearSelection() {
      this.selected = [];
    },
    // On hold waits on the requester, so it isn't flagged as late.
    isWaitingLong(ticket) {
      return (
        ticket.status !== "Closed" &&
        ticket.status !== "On hold" &&
        daysSince(ticket.created_at) >= WAITING_LONG_DAYS
      );
    },

    openCreate() {
      this.$refs.CreateTicketFormModal.open();
    },
    openTicket(ticket) {
      this.$refs.ViewTicketDetailsModal.open(ticket.id);
    },
    onCreated(ticketId) {
      this.search = "";
      clearTimeout(this.searchTimer);
      this.filter = "open";
      this.pagination.current_page = 1;
      this.scope = this.abilities.handle ? "all" : "mine";
      this.fetchTickets().then(() => this.flash(ticketId));
    },
    flash(id) {
      this.flashId = id;
      setTimeout(() => {
        if (this.flashId === id) this.flashId = null;
      }, 2500);
    },

    forwardSelected() {
      const tickets = this.forwardable;
      if (!tickets.length || this.busy) return;
      const count = tickets.length;
      const confirmed = window.confirm(
        `Forward ${count} ${count === 1 ? "ticket" : "tickets"} to General Services?\n\n` +
          "A job order referral is created for each one, and the requesters are notified."
      );
      if (!confirmed) return;

      this.busy = "forward";
      axios
        .post("api/support/tickets/to-job-order-referrals", {
          ids: tickets.map((t) => t.id),
        })
        .then((res) => {
          toastr.success(res.data.message, "Forwarded");
          this.selected = [];
          return this.fetchTickets();
        })
        .catch((err) => {
          toastr.warning(errorMessage(err), "Not forwarded");
        })
        .finally(() => {
          this.busy = "";
        });
    },
    closeSelected() {
      if (!this.closable.length) return;
      this.$refs.CloseTicketModal.open(this.closable.slice());
    },
    onBulkClosed() {
      this.selected = [];
      this.fetchTickets();
    },
    assignSelected() {
      if (!this.assignable.length) return;
      this.$refs.AssignTicketModal.open(this.assignable.slice());
    },
    onBulkAssigned() {
      this.selected = [];
      this.fetchTickets();
    },

    openFromRoute() {
      const query = this.$route.query || {};
      if (query.new === "1") {
        this.openCreate();
        this.replaceQuery({ new: undefined });
      }
      if (query.ticket) {
        this.$refs.ViewTicketDetailsModal.open(query.ticket);
      }
    },
    clearTicketQuery() {
      const query = this.$route.query || {};
      if (query.ticket || query.view) {
        this.replaceQuery({ ticket: undefined, view: undefined });
      }
    },
    replaceQuery(changes) {
      const query = { ...(this.$route.query || {}) };
      Object.keys(changes).forEach((key) => {
        if (changes[key] === undefined) delete query[key];
        else query[key] = changes[key];
      });
      this.$router.replace({ query }).catch(() => {});
    },
  },
};
</script>
